
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for endpoints to view your Transfer configuration and usage',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Program Metrics' subtitle='API reference for Transfer program metrics' compact mdxType="Header" />
    <p>{`For how-to guidance, see the `}<a parentName="p" {...{
        "href": "/docs/transfer"
      }}>{`Transfer documentation`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Program Metrics`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/metrics/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get transfer product usage metrics`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/configuration/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get transfer product configuration`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "transfermetricsget"
    }}>{`/transfer/metrics/get`}</h4>
    <Schema method='POST' route='/transfer/metrics/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/metrics/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferMetricsGetRequest = {
  originator_client_id: '61b8f48ded273e001aa8db6d',
};

try {
  const response = await client.transferMetricsGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/metrics/get \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "originator_client_id": "61b8f48ded273e001aa8db6d"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferMetricsGetRequest.new(
  {
    originator_client_id: "61b8f48ded273e001aa8db6d",
  }
)
response = client.transfer_metrics_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferMetricsGetRequest request = new TransferMetricsGetRequest()
  .originatorClientId("61b8f48ded273e001aa8db6d");
Response<TransferMetricsGetResponse> response = client()
  .transferMetricsGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferMetricsGetRequest(
    originator_client_id='61b8f48ded273e001aa8db6d',
)
response = client.transfer_metrics_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferMetricsGetRequest();
request.SetOriginatorClientId("61b8f48ded273e001aa8db6d");

response, _, err := client.PlaidApi.TransferMetricsGet(ctx).TransferMetricsGetRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferMetricsGetRequest{}
request.OriginatorClientID = plaid.String("61b8f48ded273e001aa8db6d")

response, err := client.Transfer.Metrics.Get(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/metrics/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "transferconfigurationget"
    }}>{`/transfer/configuration/get`}</h4>
    <Schema method='POST' route='/transfer/configuration/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/configuration/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferConfigurationGetRequest = {
  originator_client_id: '61b8f48ded273e001aa8db6d',
};

try {
  const response = await client.transferConfigurationGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/configuration/get \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "originator_client_id": "61b8f48ded273e001aa8db6d"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferConfigurationGetRequest.new(
  {
    originator_client_id: "61b8f48ded273e001aa8db6d",
  }
)
response = client.transfer_configuration_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferConfigurationGetRequest request = new TransferConfigurationGetRequest()
  .originatorClientId("61b8f48ded273e001aa8db6d");
Response<TransferConfigurationGetResponse> response = client()
  .transferConfigurationGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferConfigurationGetRequest(
    originator_client_id='61b8f48ded273e001aa8db6d',
)
response = client.transfer_configuration_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferConfigurationGetRequest();

request.SetOriginatorClientId("61b8f48ded273e001aa8db6d");
response, _, err := client.PlaidApi.TransferConfigurationGet(ctx).TransferConfigurationGetRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferConfigurationGetRequest{}

request.OriginatorClientID = plaid.String("61b8f48ded273e001aa8db6d")
response, err := client.Transfer.Configuration.Get(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/configuration/get' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "transfermetricsget",
        level: 4,
        title: "/transfer/metrics/get",
      },
{
        id: "transferconfigurationget",
        level: 4,
        title: "/transfer/configuration/get",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
